import {
  AttachMoneyIcon,
  BuildIcon,
  Card,
  CardActionArea,
  Grid,
  ShowChartIcon,
  Typography,
  useMediaQuery,
} from '@rbx/ui';
import React, { FC, useMemo } from 'react';
import { useTranslation } from '@rbx/intl';
import { assistantIsMobileBreakpoint } from '../constants/assistantStyleConstants';
import useExamplePromptStyles from './ExamplePrompt.styles';

export enum ExamplePromptCategory {
  Monetize = 'Monetize',
  Build = 'Build',
  Grow = 'Grow',
}

export type ExamplePromptData = {
  category: ExamplePromptCategory;
  header: string;
  prompt: string;
};

export type ExamplePromptProps = ExamplePromptData & {
  onClick: (prompt: string, category: ExamplePromptCategory) => void;
};

export type ExamplePromptIconProps = {
  category: ExamplePromptCategory;
  isCompact: boolean;
};

const ExamplePromptIcon: FC<React.PropsWithChildren<ExamplePromptIconProps>> = ({
  category,
  isCompact,
}) => {
  const {
    classes: { buildIcon, monetizeIcon },
  } = useExamplePromptStyles();
  const iconClassName = useMemo(() => {
    if (category === ExamplePromptCategory.Build) {
      return buildIcon;
    }
    if (category === ExamplePromptCategory.Monetize) {
      return monetizeIcon;
    }
    return null;
  }, [category, buildIcon, monetizeIcon]);
  const iconProps = {
    color: 'disabled',
    fontSize: isCompact ? 'medium' : 'large',
    className: iconClassName,
  } as {
    color: 'disabled';
    fontSize: 'medium' | 'large';
    className?: string;
  };
  const Icon = useMemo(() => {
    switch (category) {
      case ExamplePromptCategory.Monetize:
        return AttachMoneyIcon;
      case ExamplePromptCategory.Build:
        return BuildIcon;
      case ExamplePromptCategory.Grow:
        return ShowChartIcon;
      default:
        throw new Error(`Unhandled prompt category: ${category}`);
    }
  }, [category]);
  return <Icon {...iconProps} />;
};

const ExamplePrompt: FC<React.PropsWithChildren<ExamplePromptProps>> = ({
  category,
  header,
  prompt,
  onClick,
}) => {
  const { translate } = useTranslation();
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down(assistantIsMobileBreakpoint));
  const headerVariant = isCompact ? 'h6' : 'h4';
  const {
    classes: { card, cardActionArea, content, contentText, title, description },
  } = useExamplePromptStyles();
  const onCardClick = () => {
    onClick(prompt, category);
  };
  return (
    <Grid item Large={4} XSmall={12}>
      <Card aria-label={translate('Label.ExamplePromptCard')} className={card}>
        <div>
          <Grid className={content} container columnSpacing={1.5}>
            <Grid item Large={12} XSmall='auto'>
              <ExamplePromptIcon category={category} isCompact={isCompact} />
            </Grid>
            <Grid className={contentText} item Large={12}>
              <Typography
                aria-roledescription={translate('Label.Title')}
                className={title}
                color='primary'
                variant={headerVariant}
                component={headerVariant}>
                {header}
              </Typography>
              <Typography
                aria-roledescription={translate('Label.Prompt')}
                className={description}
                variant='body1'
                component='span'>
                {prompt}
              </Typography>
            </Grid>
          </Grid>
        </div>
        <CardActionArea
          aria-label={translate('Label.ExamplePromptCardActionArea')}
          onClick={onCardClick}
          className={cardActionArea}
        />
      </Card>
    </Grid>
  );
};

export default ExamplePrompt;
